import { SvgIconComponent } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import React, { ReactElement } from 'react';
import { ENTITY_CONSTANTS } from '../../../constants/entityConstants';
import { Icons } from '../../../icons/Icons';
import { H } from '../../../Layout';
import { formatDateTimeToDDMMYYHHMMSS } from '../../../utils/dateTime';
import { useEngagementTracker } from './EngagementContext';
import { EngagementInformation } from './EngagementInformation';

const AVAILABLE_ENGAGEMENT_TYPES = [
    ENTITY_CONSTANTS.note,
    ENTITY_CONSTANTS.production,
    ENTITY_CONSTANTS.shortlistLineitem
];

function getEngagementIcon(type: string): SvgIconComponent {
    switch (type) {
        case ENTITY_CONSTANTS.note:
            return Icons.Notes;
        case ENTITY_CONSTANTS.production:
            return Icons.WorkspacesOutlined;
        case ENTITY_CONSTANTS.shortlistLineitem:
            return Icons.Shortlist;
        default:
            return Icons.Notes;
    }
}

/**
 * Render cell used for list view representation of an engagement row.
 */
export function ListViewCell(params: GridRenderCellParams): ReactElement {
    const item = params.row;
    const { date, time } = formatDateTimeToDDMMYYHHMMSS(item.createdOn);
    const { setClickedEngagement } = useEngagementTracker();

    if (!AVAILABLE_ENGAGEMENT_TYPES.includes(item.baseEntity.type)) {
        return <></>;
    }

    const Icon = getEngagementIcon(item.baseEntity.type);
    const engagementLabel =
        item.baseEntity.type === ENTITY_CONSTANTS.shortlistLineitem
            ? item?.associatedEntity?.label
            : item.baseEntity.label;
    const shortlistId =
        item.baseEntity.type === ENTITY_CONSTANTS.shortlistLineitem ? item?.associatedEntity?.id : undefined;

    return (
        <H gap={1} sx={{ color: 'text.secondary', alignItems: 'center', mt: 1 }}>
            <IconButton size="small" component="span">
                <Icon fontSize="medium" color="primary" />
            </IconButton>
            <H
                sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 'body1.fontSize',
                    alignItems: 'center'
                }}
            >
                <EngagementInformation
                    contextTerm={item.contextTerm}
                    type={item.baseEntity.type}
                    label={engagementLabel}
                    createdByPerson={item.createdByPerson}
                    onTypeClick={() => setClickedEngagement(item)}
                    shortlistId={shortlistId}
                />
            </H>
            {(date || time) && (
                <Box textAlign="right">
                    {date && <Typography variant="caption">{date}</Typography>}
                    {time && (
                        <Typography variant="caption" display="block" sx={{ color: 'text.disabled' }}>
                            {time}
                        </Typography>
                    )}
                </Box>
            )}
        </H>
    );
}

/**
 * Factory to produce the list view configuration object for EngagementTracker DataGrid.
 */
export function getEngagementListViewConfiguration(): { enableListView: boolean; listViewColDef: GridColDef } {
    const configuration = {
        enableListView: true,
        listViewColDef: {
            field: 'listCell',
            renderCell: ListViewCell
        }
    };

    return configuration;
}
